import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Tag } from 'src/components/atoms';
import { ButtonAndTooltip } from 'src/components/molecules';
import { TagType } from 'src/graphql';
import { getSortedTags } from 'src/reducers/Tags.slice';
import { getOrderedColor } from 'src/utils';
import {
  TagsOverflowContainer,
  TagsWrapper,
} from './CaseTagsWithOverflow.styles';

export interface CaseTagsWithOverflowProps {
  tags: TagType[];
  tagsToDisplay?: number;
  size?: 'small' | 'medium';
  readOnly?: boolean;
  onTagClick?: (tagId: string) => void;
  shouldUseTooltipPortal?: boolean;
  tagsAlignment?: 'left' | 'right';
}

function tagSortFunction(
  tagA: TagType,
  tagB: TagType,
  sortedGlobalTags: TagType[],
) {
  const tagAIndex = sortedGlobalTags.findIndex((tag) => tag.id === tagA.id);
  const tagBIndex = sortedGlobalTags.findIndex((tag) => tag.id === tagB.id);

  return tagAIndex - tagBIndex;
}

export const CaseTagsWithOverflow = ({
  tags,
  tagsToDisplay = 2,
  size,
  readOnly,
  onTagClick,
  shouldUseTooltipPortal = false,
  tagsAlignment = 'left',
}: CaseTagsWithOverflowProps) => {
  const sortedGlobalTags = useSelector(getSortedTags);

  const sortedTags = useMemo(() => {
    const newTags = [...tags];
    newTags.sort((tagA, tagB) => tagSortFunction(tagA, tagB, sortedGlobalTags));
    return newTags;
  }, [tags, sortedGlobalTags]);

  const displayTags = sortedTags.slice(0, tagsToDisplay);
  const overflowTags = sortedTags.slice(tagsToDisplay);

  return (
    <TagsWrapper tagsAlignment={tagsAlignment}>
      {displayTags.map((tag) => (
        <Tag
          key={tag.id}
          text={tag.name}
          customColor={
            tag.tagType?.colorId !== undefined
              ? getOrderedColor(tag.tagType?.colorId)
              : undefined
          }
          data-testid={`${tag.name}-tag`}
          readOnly={readOnly}
          flexToFitSpace
          size={size}
          onClick={() => onTagClick?.(tag.id)}
        />
      ))}
      {overflowTags.length > 0 && (
        <ButtonAndTooltip
          shouldUsePortal={onTagClick ? false : shouldUseTooltipPortal}
          buttonProps={{
            size,
            variant: 'secondary',
            width: 'fit-content',
            children: `+${overflowTags.length}`,
            noMinWidth: true,
            'data-testid': 'tags-overflow-button',
          }}
          tooltipProps={{
            variant: 'up',
            maxWidth: '350px',
            removePadding: true,
            extraPaddingForRoot: 2,
            children: (
              <TagsOverflowContainer
                data-testid="tags-overflow-tooltip"
                maxHeight={size === 'small' ? '185px' : '200px'}
              >
                {overflowTags.map((tag) => (
                  <Tag
                    key={tag.id}
                    text={tag.name}
                    customColor={
                      tag.tagType?.colorId !== undefined
                        ? getOrderedColor(tag.tagType?.colorId)
                        : undefined
                    }
                    data-testid={`${tag.name}-tag`}
                    readOnly={readOnly}
                    flexToFitSpace
                    size={size}
                    onClick={() => onTagClick?.(tag.id)}
                  />
                ))}
              </TagsOverflowContainer>
            ),
          }}
        />
      )}
    </TagsWrapper>
  );
};
